import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { DateTime } from 'luxon';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import BtnIcon from '@/components/buttons/BtnIcon';
import classNames from 'classnames';

type VerifyRefreshProps = {
  loading: boolean;
  error: boolean;
  lastRefreshedOn: Date | null;
  onRefreshClick: () => void;
};

function VerifyRefresh({ loading, error, lastRefreshedOn, onRefreshClick }: VerifyRefreshProps) {
  const intl = useIntl();
  const [relativeTime, setRelativeTime] = useState<string | null>(null);
  const [canRefresh, setCanRefresh] = useState(false);

  useEffect(() => {
    const updateRelativeTime = () => {
      if (loading) {
        setCanRefresh(false);
        return;
      }

      if (error) {
        setCanRefresh(true);
        return;
      }

      if (!lastRefreshedOn) {
        return;
      }

      const lastRefreshedDateTime = DateTime.fromJSDate(lastRefreshedOn);

      setCanRefresh(DateTime.now().diff(lastRefreshedDateTime, 'seconds').seconds > 60);
      setRelativeTime(lastRefreshedDateTime.toRelative());
    };

    // Update immediately on mount
    updateRelativeTime();

    // Update every 10 seconds
    const intervalId = setInterval(updateRelativeTime, 10000);

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [lastRefreshedOn, error, loading]);

  return (
    <div className="flex justify-end items-center">
      {relativeTime && (
        <span className="text-xs text-zinc-500 mr-4">
          {intl.formatMessage({ id: 'misc.lastUpdated' })} {relativeTime}
        </span>
      )}
      <BtnIcon
        onClickHandler={onRefreshClick}
        disabled={!canRefresh}
        className="rounded-lg bg-white hover:bg-offWhite"
      >
        <ArrowPathIcon className={classNames('w-6 h-6', { 'animate-spin': loading })} />
      </BtnIcon>
    </div>
  );
}

export default VerifyRefresh;
